import {
  Skeleton,
  Table,
  TableRow,
  TableBody,
  TableCell,
} from "@capacit-com/lara-law-shared-ui";
import { Permission } from "@/lib/permissions/Permission.enum";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { UserWithRole } from "@/models/users/User";
import { Save, Settings, Trash2, X, Search } from "lucide-react";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { TenantRole } from "@/models/tenant/tenant";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@capacit-com/lara-law-shared-ui";
import { BulkDialog } from "./BulkDialog";
import { RolesPicker } from "./RolesPicker";
import { toast } from "@capacit-com/lara-law-shared-ui";
import useUserManagement from "@/hooks/useUserManagement";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import { FC, useState, useEffect } from "react";
import { useUserManagementContext } from "@/providers/user-management/UserManagementContext";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import UserAvatar from "@/components/images/UserAvatar";

import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import { SortableTableHeader } from "./SortableTableHeader";
import UserManagementTableActions from "./UserManagementTableActions";
import { UserManagementTabs } from "../utils/userManagementTabs";
import { EXISTING_USERS_LAYOUT } from "../utils/tableColumnWidths";
import { REFRESHING_ROWS_CLASS } from "../utils/tableLoadingState";
import {
  UserManagementSkeletonRows,
  UserManagementTableHeader,
} from "./UserManagementTableColumns";
import { type UserManagementColumn } from "../utils/userManagementColumns";

const ExistingUsersTable: FC = () => {
  /**** STATE & QUERIES ****/
  const { updateUserRoles, deleteUser } = useUserManagement();
  const {
    bulkMode,
    selectedUsers,
    setSelectedUsers,
    users,
    tenant,
    isLoadingTenant,
    isLoadingUsers,
    isRefreshingUsers,
    existingUsersPageNumber,
    existingUsersPageSize,
    existingUsersPageCount,
    setExistingUsersPageNumber,
    setExistingUsersPageSize,
    existingUsersSearch,
    setExistingUsersSearch,
    existingUsersSortBy,
    existingUsersSortAscending,
    setExistingUsersSort,
    authUser,
  } = useUserManagementContext();

  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<TenantRole[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkAssignDialogOpen, setIsBulkAssignDialogOpen] = useState(false);
  const [bulkSelectedRoles, setBulkSelectedRoles] = useState<TenantRole[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserWithRole | null>(null);
  const [searchInputValue, setSearchInputValue] = useState(existingUsersSearch);

  // Sync search input with context value
  useEffect(() => {
    setSearchInputValue(existingUsersSearch);
  }, [existingUsersSearch]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmedValue = searchInputValue.trim();
      if (trimmedValue !== existingUsersSearch) {
        setExistingUsersSearch(trimmedValue);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInputValue, existingUsersSearch, setExistingUsersSearch]);

  const handleManageRoles = (user: UserWithRole) => {
    setSelectedUser(user);
    setSelectedRoles(user.roles);
    setIsRoleDialogOpen(true);
  };

  const handleDeleteClick = (user: UserWithRole) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser.mutateAsync({ userId: userToDelete.id });
      toast.success(
        `${userToDelete.firstName} ${userToDelete.lastName} er blevet slettet`
      );
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      logger.error("Failed to delete user", {
        operation: "deleteUser",
        error,
        userId: userToDelete.id,
      });
      toast.error("Fejl ved sletning af bruger. Prøv igen.");
    }
  };

  const handleUpdateRoles = async () => {
    if (!selectedUser || !tenant) return;

    setIsUpdating(true);
    try {
      await updateUserRoles.mutateAsync({
        userId: selectedUser.id,
        tenantId: tenant.id,
        roles: selectedRoles,
        pageNumber: existingUsersPageNumber,
        pageSize: existingUsersPageSize,
      });
      toast.success(
        `Roller opdateret for ${selectedUser.firstName} ${selectedUser.lastName}`
      );
      setIsRoleDialogOpen(false);
    } catch (error) {
      logger.error("Failed to update user roles", {
        operation: "updateUserRoles",
        error,
        userId: selectedUser.id,
        roleCount: selectedRoles.length,
      });
      if (
        error instanceof Error &&
        error.message.toLowerCase().includes("system role")
      ) {
        toast.error("Du kan ikke ændre system roller.");
      } else {
        toast.error("Fejl ved opdatering af roller. Prøv igen.");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleBulkSelect = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(users.map((user) => user.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleBulkAssignRoles = async () => {
    if (selectedUsers.size === 0 || bulkSelectedRoles.length === 0) return;

    setIsBulkUpdating(true);
    try {
      const selectedUsersList = users.filter((user) =>
        selectedUsers.has(user.id)
      );
      for (const user of selectedUsersList) {
        await updateUserRoles.mutateAsync({
          userId: user.id,
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          tenantId: tenant?.id!,
          roles: bulkSelectedRoles,
          pageNumber: existingUsersPageNumber,
          pageSize: existingUsersPageSize,
        });
      }
      toast.success(
        `Roller sat for ${selectedUsers.size} bruger${
          selectedUsers.size !== 1 ? "e" : ""
        }`
      );
      setIsBulkAssignDialogOpen(false);
      setBulkSelectedRoles([]);
      setSelectedUsers(new Set());
    } catch (error) {
      logger.error("Failed to assign roles to selected users", {
        operation: "bulkAssignUserRoles",
        error,
        userCount: selectedUsers.size,
        roleCount: bulkSelectedRoles.length,
      });
      toast.error("Fejl ved tildeling af roller. Prøv igen.");
    } finally {
      setIsBulkUpdating(false);
    }
  };
  const handleSearch = () => {
    setExistingUsersSearch(searchInputValue.trim());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // The page header owns the bulk-mode flag; the selection it applies to is ours to clear.
  useEffect(() => {
    setSelectedUsers(new Set());
  }, [bulkMode, setSelectedUsers]);

  const isLoading = isLoadingUsers || isLoadingTenant;

  const columns: UserManagementColumn[] = [
    {
      key: "select",
      width: EXISTING_USERS_LAYOUT.columns.select,
      hidden: !bulkMode,
      permission: [Permission.UpdateUserRoles],
      header: (
        <Checkbox
          checked={selectedUsers.size === users.length && users.length > 0}
          onCheckedChange={handleSelectAll}
        />
      ),
      skeleton: <Skeleton className="size-4" />,
    },
    {
      key: "name",
      width: EXISTING_USERS_LAYOUT.columns.name,
      header: (
        <SortableTableHeader
          label="Navn"
          sortBy="name"
          activeSortBy={existingUsersSortBy}
          ascending={existingUsersSortAscending}
          onSort={setExistingUsersSort}
        />
      ),
      skeleton: (
        <div className="flex items-center gap-2 min-w-0">
          <Skeleton className="size-6 shrink-0" />
          <Skeleton className="h-4 w-full max-w-32" />
        </div>
      ),
    },
    {
      key: "email",
      width: EXISTING_USERS_LAYOUT.columns.email,
      header: (
        <SortableTableHeader
          label="Email"
          sortBy="email"
          activeSortBy={existingUsersSortBy}
          ascending={existingUsersSortAscending}
          onSort={setExistingUsersSort}
        />
      ),
      skeleton: <Skeleton className="h-4 w-full max-w-40" />,
    },
    {
      key: "roles",
      width: EXISTING_USERS_LAYOUT.columns.roles,
      header: "Roller",
      skeleton: <Skeleton className="h-8 w-full max-w-[140px]" />,
    },
  ];
  return (
    <div className="flex flex-col h-full gap-4 min-h-0">
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="mb-4 flex items-center justify-between gap-4 min-h-9">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Søg efter brugere..."
              className="pl-8 pr-10"
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <Button
              size="icon"
              variant="ghost"
              className="absolute right-0 top-0 h-full px-2"
              onClick={handleSearch}
              disabled={isLoadingUsers}
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
          <UserManagementTableActions tab={UserManagementTabs.USERS} />
        </div>
        {bulkMode && (
          <div
            className={cn(
              "flex items-center justify-between p-3 bg-primary/10 mb-4 rounded-md",
              selectedUsers.size === 0 && "opacity-50"
            )}
          >
            <span className="text-sm font-medium">
              {selectedUsers.size} bruger{selectedUsers.size !== 1 ? "e" : ""}{" "}
              valgt
            </span>
            <div className="flex gap-2">
              <Button
                disabled={selectedUsers.size === 0}
                size="sm"
                variant="outline"
                onClick={() => {
                  setBulkSelectedRoles([]);
                  setIsBulkAssignDialogOpen(true);
                }}
              >
                Sæt roller
              </Button>
            </div>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto border rounded-xs">
          <Table
            removeOverflowX={true}
            className="table-fixed"
            style={{ minWidth: EXISTING_USERS_LAYOUT.minWidth }}
            aria-busy={isRefreshingUsers}
          >
            <UserManagementTableHeader columns={columns} />
            <TableBody
              className={cn(isRefreshingUsers && REFRESHING_ROWS_CLASS)}
            >
              {isLoading ? (
                <UserManagementSkeletonRows columns={columns} rows={10} />
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="group">
                    {bulkMode && (
                      <PermissionGate allOf={[Permission.UpdateUserRoles]}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUsers.has(user.id)}
                            onCheckedChange={(checked) =>
                              handleBulkSelect(user.id, checked as boolean)
                            }
                          />
                        </TableCell>
                      </PermissionGate>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2 min-h-12 min-w-0">
                        <UserAvatar user={user} avatarClassName="size-6" />
                        <span className="truncate">
                          {user.firstName + " " + user.lastName}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="truncate">{user.email}</TableCell>
                    <TableCell className="relative">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {user.roles.length === 0 ? (
                            <Badge variant="secondary">Ingen rolle</Badge>
                          ) : (
                            user.roles.map((role) => {
                              return (
                                <Badge
                                  key={role.externalName}
                                  variant="outline"
                                >
                                  {role.displayName}
                                </Badge>
                              );
                            })
                          )}
                        </div>
                        <div className="flex items-center">
                          <PermissionGate allOf={[Permission.UpdateUserRoles]}>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleManageRoles(user)}
                              className="ml-2 shrink-0"
                              disabled={
                                bulkMode ||
                                user.email === authUser?.email ||
                                updateUserRoles.isPending
                              }
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate allOf={[Permission.DeleteUser]}>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDeleteClick(user)}
                              className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              disabled={
                                bulkMode ||
                                user.email === authUser?.email ||
                                deleteUser.isPending
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <DataTablePagination
        pageIndex={existingUsersPageNumber}
        pageSize={existingUsersPageSize}
        pageCount={existingUsersPageCount}
        setPageIndex={setExistingUsersPageNumber}
        setPageSize={setExistingUsersPageSize}
      />

      {/* Enkelt-bruger dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Administrer brugerroller</DialogTitle>
            <DialogDescription>
              Opdater roller for {selectedUser?.firstName}{" "}
              {selectedUser?.lastName}
            </DialogDescription>
          </DialogHeader>

          <RolesPicker
            roles={tenant?.roles || []}
            selected={selectedRoles}
            onChange={setSelectedRoles}
            idPrefix="single"
          />

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRoleDialogOpen(false)}
              disabled={isUpdating}
            >
              <X className="w-4 h-4 mr-1" />
              Annuller
            </Button>
            <Button onClick={handleUpdateRoles} disabled={isUpdating}>
              <Save className="w-4 h-4 mr-1" />
              {isUpdating ? "Opdaterer..." : "Gem ændringer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk tildel */}
      <BulkDialog
        open={isBulkAssignDialogOpen}
        onOpenChange={setIsBulkAssignDialogOpen}
        title="Sæt roller for flere brugere"
        description={`Sæt roller for ${selectedUsers.size} valgte bruger${
          selectedUsers.size !== 1 ? "e" : ""
        }`}
        confirmText="Sæt roller"
        onConfirm={handleBulkAssignRoles}
        confirmDisabled={isBulkUpdating || bulkSelectedRoles.length === 0}
        isLoading={isBulkUpdating}
      >
        <RolesPicker
          roles={tenant?.roles || []}
          selected={bulkSelectedRoles}
          onChange={setBulkSelectedRoles}
          idPrefix="bulk-assign"
        />
      </BulkDialog>

      {/* Slet bruger bekræftelse */}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        confirmPending={deleteUser.isPending}
        title="Slet bruger"
        description={`Er du sikker på, at du vil slette ${userToDelete?.firstName} ${userToDelete?.lastName}?\n\nAlle brugerens samtaler vil blive slettet.\nWorkspaces ejet af brugeren vil blive forældreløse og skal tildeles en ny ejer.`}
        attentionText="Denne handling kan ikke fortrydes!"
        confirmText="Slet bruger"
        confirmIcon={<Trash2 className="w-4 h-4 mr-1" />}
        variant="destructive"
      />
    </div>
  );
};

export default ExistingUsersTable;
